import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { ROTATE_MS, prefersReducedMotion, useRotation } from "@/components/app/featured-cities/useRotation";
import { MatchBadge } from "@/components/app/featured-cities/MatchBadge";
import { ShareMenu } from "@/components/app/featured-cities/ShareCity";
import { firmMeta } from "./firmMeta";
import { DegreesTile, EventsTile, FeedersTile, FirmCta, FirmHero, FirmKpis, RouteTile, SalaryTile, flagship } from "./FirmBlocks";

function CountryPills({ countries, code, pick }) {
  return (
    <div role="tablist" aria-label="Country" className="flex flex-wrap gap-1.5 text-sm mb-4" data-testid="featured-firms-country-tabs">
      {countries.map((c) => (
        <button key={c.code} type="button" role="tab" aria-selected={c.code === code} onClick={() => pick(c.code)}
          className={`px-3 py-1.5 rounded-full border font-medium transition-colors ${c.code === code ? "border-[#4C1D95] bg-[#4C1D95] text-white" : "border-stone-200 bg-white text-stone-700 hover:border-[#4C1D95] hover:text-[#4C1D95]"}`}
          data-testid={`featured-firms-country-${c.code.toLowerCase()}`}>{c.label}</button>
      ))}
    </div>
  );
}

function Tabs({ items, index, go, paused, cycle }) {
  const ref = useRef(null);
  useEffect(() => {
    const tab = ref.current?.children[index];
    if (tab) ref.current.scrollLeft = tab.offsetLeft - ref.current.clientWidth / 2 + tab.clientWidth / 2;
  }, [index]);
  return (
    <div className="flex items-center gap-2 sm:gap-3 pb-4 mb-6 border-b border-stone-200" data-testid="featured-firms-nav-bar">
      <div ref={ref} className="relative flex gap-1 sm:gap-2 overflow-x-auto no-scrollbar min-w-0 flex-1 scroll-smooth" role="tablist" aria-label="Firms">
        {items.map((f, i) => (
          <button key={f.slug} role="tab" aria-selected={i === index} onClick={() => go(i)}
            className={`relative shrink-0 px-3 py-2 text-sm font-medium rounded-md transition-colors ${i === index ? "text-stone-900 bg-white border border-stone-200" : "text-stone-500 hover:text-stone-900"}`}
            data-testid={`featured-firms-tab-${f.slug}`}>
            {f.name}
            {i === index && (
              <span className="absolute left-2 right-2 bottom-0.5 h-0.5 bg-stone-200 overflow-hidden rounded">
                <span key={cycle} className="block h-full featured-city-progress" style={{ background: firmMeta(f).accent, animationDuration: `${ROTATE_MS}ms`, animationPlayState: paused ? "paused" : "running" }} />
              </span>
            )}
          </button>
        ))}
      </div>
      <button onClick={() => go(index - 1)} aria-label="Previous firm" className="p-2 rounded-full border border-stone-300 hover:bg-white transition-colors" data-testid="featured-firms-prev-button"><ChevronLeft className="h-4 w-4" /></button>
      <button onClick={() => go(index + 1)} aria-label="Next firm" className="p-2 rounded-full border border-stone-300 hover:bg-white transition-colors" data-testid="featured-firms-next-button"><ChevronRight className="h-4 w-4" /></button>
    </div>
  );
}

function Spread({ f, code, match }) {
  const { formatFrom } = useCurrency();
  const m = firmMeta(f);
  const mine = match?.firms?.[f.slug];
  const o = flagship(f);
  const badge = <MatchBadge match={mine} prefs={[]} title={`How ${f.name} fits you`} withLifestyle={false} tid="featured-firms" />;
  const card = {
    tid: "featured-firms", accent: m.accent, photo: m.photo, kicker: `Featured firm · ${f.sector_label}`, title: f.name,
    subtitle: f.feeder_unis ? `Hires from ${f.feeder_unis} universities in ${f.feeder_countries} countries` : "How to get in",
    stats: [[o ? formatFrom(o.base_annual, o.currency) : "—", "Starting base"], [f.feeder_unis || "—", "Feeder universities"],
      [f.primer.bar, "Typical A-levels"], [f.offices.length || "—", "Offices"]],
    chips: f.feeders.slice(0, 3).map((x) => x.name.replace(/^University of /, "").replace(/ \(.*\)$/, "")),
    quote: null,
    match: mine?.score != null ? {
      score: mine.score,
      reasons: [...mine.parts].sort((a, b) => b.score - a.score).slice(0, 2).map((p) => `${p.label}: ${p.reason}`),
    } : null,
  };
  const share = <ShareMenu name={`firm-${f.slug}`} title={f.name} card={card} tid="featured-firms" label="Share this firm"
    url={`${window.location.origin}/?firm=${f.slug}&firm_country=${code}`}
    text={`How to get into ${f.name}: ${f.feeder_unis ? `graduates from ${f.feeder_unis} universities` : "the route in"}${o ? `, starting at ${card.stats[0][0]}` : ""}. See it on Which University Advisor`} />;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6">
      <FirmHero f={f} badge={badge} />
      <FirmKpis f={f} />
      <FeedersTile f={f} accent={m.accent} />
      <SalaryTile f={f} accent={m.accent} />
      <DegreesTile f={f} accent={m.accent} />
      <RouteTile f={f} accent={m.accent} />
      <FirmCta f={f} share={share} />
      <EventsTile f={f} accent={m.accent} />
    </div>
  );
}

export default function FeaturedFirmsPromo() {
  const [data, setData] = useState(null);
  const [match, setMatch] = useState(null);
  const [params] = useSearchParams();
  const deep = params.get("firm");
  const [code, setCode] = useState(params.get("firm_country")?.toUpperCase() || "UK");
  const picked = useRef(Boolean(params.get("firm_country")));
  const { user } = useAuth();
  const ref = useRef(null);
  const reduced = prefersReducedMotion();
  const country = data?.countries.find((c) => c.code === code) || data?.countries[0];
  const items = useMemo(() => country?.firms || [], [country]);
  const initial = useMemo(() => Math.max(0, items.findIndex((f) => f.slug === deep)), [items, deep]);
  const { index, paused, setPaused, go, cycle } = useRotation(items.length, initial);

  useEffect(() => { api.get("/firms/featured").then(({ data: d }) => setData(d)).catch(() => setData(null)); }, []);
  useEffect(() => {
    if (!data || !deep || picked.current) return;
    const hit = data.countries.find((c) => c.firms.some((x) => x.slug === deep));
    if (hit) { picked.current = true; setCode(hit.code); }
  }, [data, deep]);
  useEffect(() => {
    api.get("/geo/country").then((r) => {
      const c = r.data?.country;
      if (!picked.current && c) setCode((cur) => (["UK", "US", "IE", "DE", "FR", "IT", "ES", "PT"].includes(c) ? c : cur));
    }).catch(() => {});
  }, []);
  const loadMatch = useCallback(() => {
    if (!user) { setMatch(null); return; }
    api.get("/firms/featured/match").then(({ data: d }) => setMatch(d)).catch(() => setMatch(null));
  }, [user]);
  useEffect(() => { loadMatch(); }, [loadMatch]);
  useEffect(() => { if (deep && items.length && ref.current) ref.current.scrollIntoView({ behavior: reduced ? "auto" : "smooth" }); }, [deep, items.length, reduced]);

  if (!items.length) return null;
  const f = items[Math.min(index, items.length - 1)];
  const pick = (c) => { picked.current = true; setCode(c); go(0); };
  const usLike = country.code === "UK" || country.code === "US";
  return (
    <section ref={ref} id="featured-firms" className="border-b border-stone-200 bg-[#FBF8F3] overflow-x-hidden scroll-mt-40" data-testid="landing-firm-tiles-section"
      tabIndex={0} aria-roledescription="carousel" aria-label="Featured firms"
      onKeyDown={(e) => { if (e.target !== e.currentTarget) return; if (e.key === "ArrowRight") go(index + 1); if (e.key === "ArrowLeft") go(index - 1); }}
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)} onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setPaused(false); }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20" data-testid="featured-firms-promo-section">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-2 mb-6">
          <div>
            <p className="font-data text-xs uppercase tracking-widest text-stone-500">How to get into</p>
            <h2 className="font-editorial text-3xl sm:text-4xl text-stone-900">Where do you want to work?</h2>
          </div>
          <p className="text-sm text-stone-500 sm:text-right max-w-sm">Pick a country, then a firm: the universities and degrees that feed graduates in, starting pay and the route to an offer.</p>
        </div>
        <CountryPills countries={data.countries} code={country.code} pick={pick} />
        <Tabs items={items} index={index} go={go} paused={paused} cycle={cycle} />
        <AnimatePresence mode="wait">
          <motion.div key={`${country.code}-${f.slug}`} initial={reduced ? false : { opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            exit={reduced ? { opacity: 1 } : { opacity: 0, y: -12 }} transition={{ duration: reduced ? 0 : 0.35, ease: "easeOut" }}
            aria-live="polite" data-testid={`featured-firms-spread-${f.slug}`}>
            <Spread f={f} code={country.code} match={match} />
          </motion.div>
        </AnimatePresence>
        <div className="mt-8 flex flex-wrap items-center gap-x-3 gap-y-2">
          <Link to="/firm-guides" className="text-sm font-medium text-[#4C1D95] hover:underline" data-testid="landing-firm-tiles-see-all">
            Browse all {data.catalog_total.toLocaleString()} firms →
          </Link>
          <span className="text-stone-400">·</span>
          <Link to={usLike ? `/get-me-to/${f.canonical_slug}` : f.guide_url} className="text-sm font-medium text-stone-600 hover:text-[#4C1D95]" data-testid="featured-firms-reverse-link">
            {usLike ? "Or reverse-search: pick a firm, get a shortlist →" : `Or dive straight into ${f.name} →`}
          </Link>
        </div>
      </div>
    </section>
  );
}
